/** Pure formatting + labelling helpers for the HUD (unit-tested). */

export const MS_TO_MPH = 2.23694;

export function mph(metresPerSecond: number): number {
  return Math.round(Math.abs(metresPerSecond) * MS_TO_MPH);
}

/** Metres (rounded to 5) under 1000, kilometres above. */
export function formatDistance(m: number): string {
  if (!Number.isFinite(m)) return '';
  if (m < 1000) return `${Math.max(0, Math.round(m / 5) * 5)} m`;
  return `${(m / 1000).toFixed(1)} km`;
}

export function formatClock(seconds: number): string {
  const s = Math.max(0, Math.ceil(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export const HEAT_NAMES = ['Unnoticed', 'Suspected', 'In pursuit', 'Roadblocks', 'City-wide'] as const;

export function heatName(level: number): string {
  return HEAT_NAMES[Math.max(0, Math.min(4, Math.round(level)))]!;
}

/** Typewriter-face status line under the heat badge. */
export function heatState(level: number, seen: boolean): string {
  if (level <= 0) return 'Nobody is looking';
  if (seen) return level >= 3 ? 'They have you boxed in' : 'Spotted — keep moving';
  return level >= 3 ? 'Every unit is hunting' : 'They lost you — stay out of sight';
}

/** Plain words for a forced crash ("laid on the tarmac", docs/POLICE.md §14; the toast): what happened and what it got you. */
export function unitDownToast(e: { downs: number; needed: number; dropped: boolean; searchNow: boolean }): string {
  if (e.dropped) return 'Police car out of the chase. Heat down a level.';
  const more = e.needed > e.downs ? e.needed - e.downs : 0;
  const head = e.searchNow ? 'Police car out of the chase. They’ve lost sight of you.' : 'Police car out of the chase.';
  return more > 0 ? `${head} ${more} more and the heat drops.` : head;
}

/** Which result stamp text a mission failure reason maps to. */
export function failStamp(reason: string): { kind: 'failed' | 'busted' | 'wasted'; stamp: string } {
  const r = reason.toLowerCase();
  if (/(nick|bust|arrest|caught)/.test(r)) return { kind: 'busted', stamp: 'NICKED' };
  if (/(wast|dead|died|killed)/.test(r)) return { kind: 'wasted', stamp: 'WASTED' };
  return { kind: 'failed', stamp: 'JOB BLOWN' };
}

/** Human label for a KeyboardEvent.code. */
export function keyLabel(code: string): string {
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  const map: Record<string, string> = {
    Space: 'Space',
    Enter: 'Enter',
    Escape: 'Esc',
    ShiftLeft: 'Shift',
    ShiftRight: 'Shift',
    ControlLeft: 'Ctrl',
    ControlRight: 'Ctrl',
    ArrowUp: '↑',
    ArrowDown: '↓',
    ArrowLeft: '←',
    ArrowRight: '→',
    Tab: 'Tab',
    Backquote: '`',
    Mouse0: 'Left click',
    Mouse1: 'Middle click',
    Mouse2: 'Right click',
    WheelUp: 'Wheel',
    WheelDown: 'Wheel',
  };
  return map[code] ?? code;
}

/** The keys bound to one action, as the player reads them: "Shift", "Space", "Q / Space" (unique labels). */
export function bindingLabel(codes: readonly string[]): string {
  return [...new Set(codes.map(keyLabel))].join(' / ');
}

/** Next enabled index when moving `dir` (+1/-1) through `enabled`, wrapping. Returns `from` if nothing is enabled. */
export function stepFocus(from: number, dir: 1 | -1, enabled: readonly boolean[]): number {
  const n = enabled.length;
  if (n === 0) return 0;
  for (let i = 1; i <= n; i++) {
    const idx = (((from + dir * i) % n) + n) % n;
    if (enabled[idx]) return idx;
  }
  return from;
}

/** Key prompts written for a keyboard, reworded for the on-screen buttons (docs/MOBILE.md). */
const TOUCH_WORDS: [RegExp, string][] = [
  [/\bpress E\b/g, 'tap USE'],
  [/\bpress R\b/g, 'tap RADIO'],
  [/\bpress Enter\b/g, 'tap to continue'],
  [/\bpress M\b/g, 'tap MAP'],
  [/\bpress I\b/g, 'tap the BOOK'],
  // At the start of a sentence too: the online ladder's "Press E next to it" reached a phone as it was.
  [/\bPress E\b/g, 'Tap USE'],
  [/\bPress M\b/g, 'Tap MAP'],
  [/\bPress I\b/g, 'Tap the BOOK'],
];

/** `text` unchanged on a keyboard; with the touch UI on, "press E" becomes "tap USE" and so on. */
export function touchWording(text: string, touch: boolean): string {
  if (!touch) return text;
  for (const [re, to] of TOUCH_WORDS) text = text.replace(re, to);
  return text;
}
